import { useState, useEffect, useMemo } from 'react'
import { api, DirectoryProvider } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { useDatePreferences } from '../hooks/useDatePreferences'
import { useToast } from '../components/Toast'
import { Modal } from '../components/Modal'
import { FormField } from '../components/FormField'
import CsvImportModal from '../components/CsvImportModal'
import {
  Users as UsersIcon,
  Plus,
  Shield,
  Mail,
  Clock,
  Pencil,
  Trash2,
  AlertTriangle,
  Upload,
  FileSpreadsheet,
  Building2,
  RefreshCw,
  Filter,
  CheckSquare,
  Square,
  UserCheck,
  UserX,
} from 'lucide-react'

interface User {
  id: string
  username: string
  email: string
  role: string
  active: number
  first_name?: string
  last_name?: string
  department?: string
  source?: DirectoryProvider | 'manual'
  last_synced_at?: number
  created_at: number
}

type ModalMode = 'create' | 'edit' | null

interface FormErrors {
  username?: string
  email?: string
  role?: string
}

export default function Users() {
  const { user: currentUser } = useAuth()
  const { formatDate } = useDatePreferences()
  const toast = useToast()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [modalMode, setModalMode] = useState<ModalMode>(null)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [deleteConfirm, setDeleteConfirm] = useState<User | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Import modal
  const [showImportModal, setShowImportModal] = useState(false)

  // Filters
  const [sourceFilter, setSourceFilter] = useState<string>('all')
  const [departmentFilter, setDepartmentFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Bulk selection
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [bulkUpdating, setBulkUpdating] = useState(false)

  const [form, setForm] = useState({
    username: '',
    email: '',
    role: 'viewer',
    firstName: '',
    lastName: '',
    department: '',
    title: '',
    active: true,
  })

  async function fetchUsers() {
    try {
      const res = await api.getUsers()
      if (res.success) {
        setUsers(res.users)
      }
    } catch (err) {
      console.error('Failed to load users:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  // Get unique departments from users
  const departments = useMemo(() => {
    const depts = new Set<string>()
    users.forEach(u => {
      if (u.department) depts.add(u.department)
    })
    return Array.from(depts).sort()
  }, [users])

  // Filter users
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Source filter
      if (sourceFilter !== 'all') {
        const userSource = user.source || 'manual'
        if (userSource !== sourceFilter) return false
      }

      // Department filter
      if (departmentFilter !== 'all') {
        if (user.department !== departmentFilter) return false
      }

      // Search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const matchesName = user.username.toLowerCase().includes(query)
        const matchesEmail = user.email.toLowerCase().includes(query)
        const matchesFirstName = user.first_name?.toLowerCase().includes(query)
        const matchesLastName = user.last_name?.toLowerCase().includes(query)
        if (!matchesName && !matchesEmail && !matchesFirstName && !matchesLastName) {
          return false
        }
      }

      return true
    })
  }, [users, sourceFilter, departmentFilter, searchQuery])

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set())
    } else {
      setSelectedUsers(new Set(filteredUsers.map(u => u.id)))
    }
  }

  const toggleSelectUser = (userId: string) => {
    const newSelected = new Set(selectedUsers)
    if (newSelected.has(userId)) {
      newSelected.delete(userId)
    } else {
      newSelected.add(userId)
    }
    setSelectedUsers(newSelected)
  }

  const handleBulkStatusChange = async (active: boolean) => {
    if (selectedUsers.size === 0) return

    setBulkUpdating(true)
    try {
      const res = await api.bulkUpdateUserStatus(Array.from(selectedUsers), active)
      if (res.success) {
        toast.success(`Updated ${res.updated} users`)
        setSelectedUsers(new Set())
        await fetchUsers()
      }
    } catch (err) {
      toast.error('Failed to update users')
    } finally {
      setBulkUpdating(false)
    }
  }

  // Source icon component
  const SourceIcon = ({ source }: { source?: string }) => {
    switch (source) {
      case 'csv':
        return <FileSpreadsheet className="h-4 w-4 text-gray-500" />
      case 'google':
        return (
          <svg className="h-4 w-4" viewBox="0 0 24 24">
            <path fill="#EA4335" d="M5.26620003,9.76452941 C6.19878754,6.93863203 8.85444915,4.90909091 12,4.90909091 C13.6909091,4.90909091 15.2181818,5.50909091 16.4181818,6.49090909 L19.9090909,3 C17.7818182,1.14545455 15.0545455,0 12,0 C7.27006974,0 3.1977497,2.69829785 1.23999023,6.65002441 L5.26620003,9.76452941 Z"/>
            <path fill="#34A853" d="M16.0407269,18.0125889 C14.9509167,18.7163016 13.5660892,19.0909091 12,19.0909091 C8.86648613,19.0909091 6.21911939,17.076871 5.27698177,14.2678769 L1.23746264,17.3349879 C3.19279051,21.2936293 7.26500293,24 12,24 C14.9328362,24 17.7353462,22.9573905 19.834192,20.9995801 L16.0407269,18.0125889 Z"/>
            <path fill="#4A90E2" d="M19.834192,20.9995801 C22.0291676,18.9520994 23.4545455,15.903663 23.4545455,12 C23.4545455,11.2909091 23.3454545,10.5272727 23.1818182,9.81818182 L12,9.81818182 L12,14.4545455 L18.4363636,14.4545455 C18.1187732,16.013626 17.2662994,17.2212117 16.0407269,18.0125889 L19.834192,20.9995801 Z"/>
            <path fill="#FBBC05" d="M5.27698177,14.2678769 C5.03832634,13.556323 4.90909091,12.7937589 4.90909091,12 C4.90909091,11.2182781 5.03443647,10.4668121 5.26620003,9.76452941 L1.23999023,6.65002441 C0.43658717,8.26043162 0,10.0753848 0,12 C0,13.9195484 0.444780743,15.7301709 1.23746264,17.3349879 L5.27698177,14.2678769 Z"/>
          </svg>
        )
      case 'entra':
        return (
          <svg className="h-4 w-4" viewBox="0 0 23 23">
            <path fill="#f35325" d="M1 1h10v10H1z"/>
            <path fill="#81bc06" d="M12 1h10v10H12z"/>
            <path fill="#05a6f0" d="M1 12h10v10H1z"/>
            <path fill="#ffba08" d="M12 12h10v10H12z"/>
          </svg>
        )
      default:
        return <UsersIcon className="h-4 w-4 text-gray-400" />
    }
  }

  const validateForm = (): boolean => {
    const errors: FormErrors = {}

    if (modalMode === 'create') {
      if (!form.username.trim()) {
        errors.username = 'Username is required'
      } else if (form.username.length < 3) {
        errors.username = 'Username must be at least 3 characters'
      } else if (!/^[a-zA-Z0-9_-]+$/.test(form.username)) {
        errors.username = 'Username can only contain letters, numbers, underscores, and hyphens'
      }

      if (!form.email.trim()) {
        errors.email = 'Email is required'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        errors.email = 'Please enter a valid email address'
      }
    }

    if (!form.role) {
      errors.role = 'Role is required'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const openCreateModal = () => {
    setForm({ username: '', email: '', role: 'viewer', firstName: '', lastName: '', department: '', title: '', active: true })
    setSelectedUser(null)
    setError('')
    setFormErrors({})
    setModalMode('create')
  }

  const openEditModal = (user: User) => {
    setForm({
      username: user.username,
      email: user.email,
      role: user.role,
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      department: (user as any).department || '',
      title: (user as any).title || '',
      active: !!user.active,
    })
    setSelectedUser(user)
    setError('')
    setFormErrors({})
    setModalMode('edit')
  }

  const closeModal = () => {
    setModalMode(null)
    setSelectedUser(null)
    setError('')
    setFormErrors({})
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setError('')
    setSaving(true)

    try {
      if (modalMode === 'create') {
        const res = await api.createUser({
          username: form.username,
          email: form.email,
          role: form.role,
          firstName: form.firstName || undefined,
          lastName: form.lastName || undefined,
          department: form.department || undefined,
          title: form.title || undefined,
        }) as { success: boolean; user?: unknown; error?: string }
        if (!res.success) {
          setError(res.error || 'Failed to create user')
          return
        }
      } else if (modalMode === 'edit' && selectedUser) {
        const res = await api.updateUser(selectedUser.id, {
          role: form.role,
          active: form.active,
          firstName: form.firstName || undefined,
          lastName: form.lastName || undefined,
          department: form.department || undefined,
          title: form.title || undefined,
        }) as { success: boolean; user?: unknown; error?: string }
        if (!res.success) {
          setError(res.error || 'Failed to update user')
          return
        }
      }
      closeModal()
      fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    setDeleting(true)

    try {
      const res = await api.deleteUser(deleteConfirm.id)
      if (res.success) {
        setDeleteConfirm(null)
        fetchUsers()
      } else {
        setError('Failed to delete user')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user')
    } finally {
      setDeleting(false)
    }
  }

  const roleColors: Record<string, string> = {
    admin: 'badge-red',
    contributor: 'badge-blue',
    viewer: 'badge-gray',
  }

  // formatDate is now provided by useDatePreferences hook

  const isCurrentUser = (user: User) => currentUser?.id === user.id

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-500">Manage team members and access</p>
        </div>
        {currentUser?.role === 'admin' && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowImportModal(true)}
              className="btn-secondary flex items-center"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import Users
            </button>
            <button className="btn-primary" onClick={openCreateModal}>
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="flex-1 min-w-[200px] max-w-md">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Source Filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          >
            <option value="all">All Sources</option>
            <option value="manual">Manual</option>
            <option value="csv">CSV Import</option>
            <option value="google">Google Workspace</option>
            <option value="entra">Microsoft Entra</option>
          </select>
        </div>

        {/* Department Filter */}
        {departments.length > 0 && (
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          >
            <option value="all">All Departments</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
        )}

        {/* Bulk Actions */}
        {selectedUsers.size > 0 && currentUser?.role === 'admin' && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-gray-500">{selectedUsers.size} selected</span>
            <button
              onClick={() => handleBulkStatusChange(true)}
              disabled={bulkUpdating}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-green-700 bg-green-50 hover:bg-green-100 rounded-lg disabled:opacity-50"
            >
              <UserCheck className="h-4 w-4" />
              Enable
            </button>
            <button
              onClick={() => handleBulkStatusChange(false)}
              disabled={bulkUpdating}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-700 bg-red-50 hover:bg-red-100 rounded-lg disabled:opacity-50"
            >
              <UserX className="h-4 w-4" />
              Disable
            </button>
          </div>
        )}
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {users.length === 0 ? 'No users found' : 'No users match your filters'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {currentUser?.role === 'admin' && (
                    <th className="px-3 py-3 text-left">
                      <button
                        onClick={toggleSelectAll}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {selectedUsers.size === filteredUsers.length ? (
                          <CheckSquare className="h-5 w-5" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  {currentUser?.role === 'admin' && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className={`hover:bg-gray-50 ${selectedUsers.has(user.id) ? 'bg-primary-50' : ''}`}>
                    {currentUser?.role === 'admin' && (
                      <td className="px-3 py-4">
                        <button
                          onClick={() => toggleSelectUser(user.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {selectedUsers.has(user.id) ? (
                            <CheckSquare className="h-5 w-5 text-primary-600" />
                          ) : (
                            <Square className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-700">
                            {user.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {user.first_name && user.last_name
                              ? `${user.first_name} ${user.last_name}`
                              : user.username}
                            {isCurrentUser(user) && (
                              <span className="ml-2 text-xs text-gray-400">(you)</span>
                            )}
                          </p>
                          <p className="text-sm text-gray-500 flex items-center">
                            <Mail className="h-3 w-3 mr-1" />
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.department ? (
                        <span className="flex items-center gap-1 text-sm text-gray-600">
                          <Building2 className="h-3 w-3" />
                          {user.department}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`${roleColors[user.role] || 'badge-gray'} capitalize`}>
                        <Shield className="h-3 w-3 mr-1 inline" />
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={user.active ? 'badge-green' : 'badge-red'}
                      >
                        {user.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2" title={user.source || 'manual'}>
                        <SourceIcon source={user.source} />
                        {user.last_synced_at && (
                          <span className="text-xs text-gray-400" title={`Last synced: ${formatDate(user.last_synced_at)}`}>
                            <RefreshCw className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <Clock className="h-4 w-4 inline mr-1" />
                      {formatDate(user.created_at)}
                    </td>
                    {currentUser?.role === 'admin' && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(user)}
                            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            title="Edit user"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          {!isCurrentUser(user) && (
                            <button
                              onClick={() => setDeleteConfirm(user)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete user"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit User Modal */}
      <Modal
        isOpen={!!modalMode}
        onClose={closeModal}
        title={modalMode === 'create' ? 'Add User' : 'Edit User'}
        maxWidth="max-w-md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {error}
            </div>
          )}

          {modalMode === 'create' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="First Name">
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    className="input"
                    disabled={saving}
                  />
                </FormField>
                <FormField label="Last Name">
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    className="input"
                    disabled={saving}
                  />
                </FormField>
              </div>

              <FormField label="Username" required error={formErrors.username}>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => {
                    setForm({ ...form, username: e.target.value })
                    if (formErrors.username) {
                      setFormErrors({ ...formErrors, username: undefined })
                    }
                  }}
                  className={`input ${formErrors.username ? 'border-red-500 focus:ring-red-500' : ''}`}
                  disabled={saving}
                  aria-invalid={!!formErrors.username}
                  aria-describedby={formErrors.username ? 'username-error' : undefined}
                />
              </FormField>

              <FormField label="Email" required error={formErrors.email}>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => {
                    setForm({ ...form, email: e.target.value })
                    if (formErrors.email) {
                      setFormErrors({ ...formErrors, email: undefined })
                    }
                  }}
                  className={`input ${formErrors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                  disabled={saving}
                  aria-invalid={!!formErrors.email}
                  aria-describedby={formErrors.email ? 'email-error' : undefined}
                />
              </FormField>

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Department">
                  <input
                    type="text"
                    value={form.department}
                    onChange={(e) => setForm({ ...form, department: e.target.value })}
                    className="input"
                    placeholder="e.g., Engineering"
                    disabled={saving}
                  />
                </FormField>
                <FormField label="Job Title">
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="input"
                    placeholder="e.g., Software Engineer"
                    disabled={saving}
                  />
                </FormField>
              </div>
            </>
          )}

          {modalMode === 'edit' && selectedUser && (
            <>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{selectedUser.username}</span>
                  <span className="mx-2">-</span>
                  {selectedUser.email}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField label="First Name">
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    className="input"
                    disabled={saving}
                  />
                </FormField>
                <FormField label="Last Name">
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    className="input"
                    disabled={saving}
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Department">
                  <input
                    type="text"
                    value={form.department}
                    onChange={(e) => setForm({ ...form, department: e.target.value })}
                    className="input"
                    placeholder="e.g., Engineering"
                    disabled={saving}
                  />
                </FormField>
                <FormField label="Job Title">
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="input"
                    placeholder="e.g., Software Engineer"
                    disabled={saving}
                  />
                </FormField>
              </div>

              {!isCurrentUser(selectedUser) && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Account Status</p>
                    <p className="text-xs text-gray-500">Inactive users cannot log in</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      className="sr-only peer"
                      disabled={saving}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
              )}
            </>
          )}

          <FormField label="Role" required error={formErrors.role}>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className={`input ${formErrors.role ? 'border-red-500 focus:ring-red-500' : ''}`}
              disabled={saving || (modalMode === 'edit' && !!selectedUser && isCurrentUser(selectedUser))}
            >
              <option value="viewer">Viewer - Read-only access</option>
              <option value="contributor">Contributor - Can add evidence</option>
              <option value="admin">Admin - Full access</option>
            </select>
            {modalMode === 'edit' && selectedUser && isCurrentUser(selectedUser) && (
              <p className="text-xs text-gray-500 mt-1">You cannot change your own role</p>
            )}
          </FormField>

          {modalMode === 'create' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700 flex items-center">
                <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                The user will receive a welcome email with login instructions.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={closeModal}
              className="btn-secondary"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : modalMode === 'create' ? 'Create User' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        showHeader={false}
        maxWidth="max-w-md"
      >
        <div className="p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
            Delete User
          </h3>
          <p className="text-sm text-gray-500 text-center mb-6">
            Are you sure you want to delete <span className="font-medium text-gray-900">{deleteConfirm?.username}</span>?
            This action cannot be undone.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setDeleteConfirm(null)}
              className="flex-1 btn-secondary"
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {deleting ? 'Deleting...' : 'Delete User'}
            </button>
          </div>
        </div>
      </Modal>

      {/* CSV Import Modal */}
      <CsvImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          setShowImportModal(false)
          fetchUsers()
        }}
      />
    </div>
  )
}
